/**
 * FDV — interacción en la página de producto (`fdv_pdp_interaction`).
 *
 * Un solo evento con un campo `tipo` (no una docena de nombres). Cada combinación `tipo` +
 * `detalle` se manda UNA vez por vista de producto; la galería, hasta 10; tope duro de 30 por
 * vista. Solo carga en la plantilla `product` (ver `snippets/fdv-engagement.liquid`).
 *
 * No duplica lo que ya existe: `product_added_to_cart`, `fdv_ritual_revealed`,
 * `fdv_review_summary_click` y `fdv_promo_bundle_added` siguen siendo la fuente de esos hechos.
 * Sin datos personales: nada de lo que escribe la persona, solo nombres de bloque e índices.
 *
 * Especificación: flor_venus_comercial_intelligence,
 * 2026/10-octubre/campanas/2026-10-07_alcance-y-captacion/eventos-nuevos.md (§5).
 */
(() => {
  const FDV = window.FDV;
  if (!FDV || typeof FDV.track !== 'function' || FDV.pdp) return;

  let cfg = {};
  try {
    cfg = JSON.parse(document.getElementById('fdv-analytics-config')?.textContent || '{}');
  } catch (e) {
    /* noop */
  }
  if (cfg.page_type !== 'product') return;

  const V = 1;
  const CAP = 30;
  const GALERIA_CAP = 10;
  const VISIBLE_MS = 1000;

  // `selector` → bloque. El de reseñas es de una app: se confirma en el tema de desarrollo.
  const BLOQUES = {
    galeria: 'media-gallery',
    precio_compra: 'product-form-component',
    descripcion: 'fdv-product-description',
    ritual: 'fdv-ritual-reveal',
    acordeon: 'accordion-custom',
    resenas: '[data-fdv-reviews], .fdv-reviews, [id*="fdv-reviews"], [class*="flor-de-venus-reviews"]',
    recomendados: 'product-recommendations',
  };

  const sent = new Set();
  let total = 0;
  let galeriaCount = 0;
  let orden = 0;

  FDV.pdp = { v: V };

  const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || null;

  const ms = () => Math.round(FDV.engagement?.visibleMs?.() ?? performance.now());
  const pct = () => FDV.engagement?.scrollMaxPct?.() ?? null;

  function variantId() {
    const input = document.querySelector('product-form-component input[name="id"]');
    return input?.value || new URLSearchParams(location.search).get('variant') || null;
  }

  function emit(tipo, detalle, extra) {
    if (FDV.consent?.analytics === false) return;
    const key = `${tipo}:${detalle}`;
    if (sent.has(key) || total >= CAP) return;
    if (tipo === 'galeria') {
      if (galeriaCount >= GALERIA_CAP) return;
      galeriaCount += 1;
    }
    sent.add(key);
    total += 1;
    orden += 1;
    try {
      FDV.track('fdv_pdp_interaction', {
        tipo,
        detalle: String(detalle),
        product_id: cfg.product_id ?? null,
        product_handle: handle,
        variant_id: variantId(),
        orden,
        ms_desde_carga: ms(),
        scroll_max_pct: pct(),
        device: matchMedia('(max-width: 749px)').matches || 'ontouchstart' in window ? 'mobile' : 'desktop',
        v: V,
        ...extra,
      });
    } catch (e) {
      /* el tracking nunca debe romper la página */
    }
  }

  const slug = (s) =>
    String(s || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 30);

  // --- Bloques vistos (≥ 50% visible, o ≥ 50% de la pantalla si el bloque es más alto, durante ≥ 1 s) ---
  const watched = new WeakSet();
  const timers = new Map();
  let io = null;

  function onIntersect(entries) {
    for (const entry of entries) {
      const name = entry.target.dataset.fdvPdpBlock;
      const visible = entry.isIntersecting && (entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= innerHeight * 0.5);
      if (visible && !timers.has(entry.target)) {
        timers.set(
          entry.target,
          setTimeout(() => {
            timers.delete(entry.target);
            emit('bloque_visto', name);
          }, VISIBLE_MS)
        );
      } else if (!visible && timers.has(entry.target)) {
        clearTimeout(timers.get(entry.target));
        timers.delete(entry.target);
      }
    }
  }

  function watchBlocks() {
    if (!('IntersectionObserver' in window)) return;
    io = io || new IntersectionObserver(onIntersect, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    // Todos los bloques viven en #MainContent (el pie de página también tiene acordeones).
    const root = document.getElementById('MainContent') || document;
    for (const [name, selector] of Object.entries(BLOQUES)) {
      let el = null;
      try {
        el = root.querySelector(selector);
      } catch (e) {
        el = null;
      }
      if (!el || watched.has(el)) continue;
      watched.add(el);
      el.dataset.fdvPdpBlock = name;
      io.observe(el);
    }
  }

  // Algunos bloques (reseñas de la app, recomendaciones) llegan después de la carga.
  const scan = () => watchBlocks();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  setTimeout(scan, 1500);
  setTimeout(scan, 4000);

  // --- Acciones ---------------------------------------------------------------
  const galleryOf = (el) => el?.closest?.('media-gallery') || null;
  const inCart = (el) => !!el?.closest?.('cart-items-component, theme-drawer, cart-drawer-component');

  function slideIndex(slide) {
    const gallery = galleryOf(slide);
    const slides = gallery ? [...gallery.querySelectorAll('slideshow-slide')] : [];
    const i = slides.indexOf(slide);
    return i >= 0 ? i + 1 : null;
  }

  document.addEventListener('slideshow:select', (e) => {
    const gallery = galleryOf(e.target);
    if (!gallery) return;
    const d = e.detail || {};
    if (!d.userInitiated && d.trigger === 'select') return; // la foto inicial no cuenta
    emit('galeria', (d.index ?? 0) + 1, {
      total_medios: gallery.querySelectorAll('slideshow-slide').length,
      medio: d.slide?.querySelector?.('video') ? 'video' : 'imagen',
    });
  });

  document.addEventListener('zoom-media:selected', (e) => {
    const i = e.detail?.index;
    if (typeof i === 'number') emit('zoom', i + 1);
  });

  document.addEventListener('media:started-playing', (e) => {
    const slide = e.detail?.resource?.closest?.('slideshow-slide');
    emit('video', (slide && slideIndex(slide)) || 'otro');
  });

  // El selector de variantes de Horizon avisa con `shopify:product:select`
  // (`selectedOptions` en el evento; `detail.variantId` cuando se eligió por id; verificado en desarrollo).
  document.addEventListener('shopify:product:select', (e) => {
    if (!(e.target instanceof Element) || e.target.closest('product-card, product-recommendations')) return;
    const opts = e.selectedOptions || e.detail?.selectedOptions;
    const detalle = Array.isArray(opts)
      ? opts
          .map((o) => o?.value ?? o)
          .filter((v) => typeof v === 'string')
          .join(' / ')
      : '';
    // El formulario aún no se actualiza: el id de la variante nueva viene en el propio evento.
    const nuevo = e.detail?.variantId;
    emit('variante', slug(detalle) || 'cambio', nuevo ? { variant_id: String(nuevo) } : undefined);
  });

  document.addEventListener('quantity-selector:update', (e) => {
    if (inCart(e.target) || e.detail?.cartLine != null) return;
    if (typeof e.detail?.quantity === 'number') emit('cantidad', e.detail.quantity);
  });

  // `toggle` no burbujea: se escucha en captura. Solo las filas del acordeón del producto.
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open) return;
      if (!d.closest('#MainContent accordion-custom')) return;
      emit('acordeon', slug(d.querySelector('summary')?.textContent) || 'fila');
    },
    true
  );

  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('product-recommendations a[href*="/products/"]') : null;
    if (!a) return;
    const h = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    if (h) emit('recomendado_click', h[1]);
  });
})();
